/**
 * Which fog cells to compose again when a chunk finishes drawing (ADR-100 decision 63).
 */

export const FOG_CELLS_PER_CHUNK = 4;

/**
 * Throws unless a value is an integer of at least a minimum.
 *
 * @param value Value to check.
 * @param min Smallest allowed value.
 * @param label Name used in the error message.
 * @throws RangeError when the value is not an integer or is below the minimum.
 */
function assertIntegerFrom(value: number, min: number, label: string): void {
  if (!Number.isInteger(value) || value < min) {
    throw new RangeError(`${label} must be an integer of at least ${String(min)}, received ${String(value)}`);
  }
}

/**
 * The fog cells to compose again once a chunk has drawn: the cells over the chunk, then the ring one cell around it,
 * each once and kept on the map, so a cell whose margin was read before this chunk existed is blurred again with real
 * pixels.
 *
 * @param chunk The chunk's column and row among the map's chunks, integers of at least 0.
 * @param columns The map's width in fog cells, an integer of at least 1.
 * @param rows The map's height in fog cells, an integer of at least 1.
 * @returns Cell indexes `row * columns + column`: the chunk's own cells in row order, then the ring's in row order; empty when the chunk and its ring are wholly off the map.
 * @throws RangeError when a chunk coordinate is not a whole number of at least 0 or a map size is not a whole number of at least 1.
 */
export function cellsAfterChunk(chunk: { readonly column: number; readonly row: number }, columns: number, rows: number): number[] {
  assertIntegerFrom(chunk.column, 0, "chunk.column");
  assertIntegerFrom(chunk.row, 0, "chunk.row");
  assertIntegerFrom(columns, 1, "columns");
  assertIntegerFrom(rows, 1, "rows");
  const ownLeft = chunk.column * FOG_CELLS_PER_CHUNK;
  const ownTop = chunk.row * FOG_CELLS_PER_CHUNK;
  const ownRight = ownLeft + FOG_CELLS_PER_CHUNK;
  const ownBottom = ownTop + FOG_CELLS_PER_CHUNK;
  const left = Math.max(0, ownLeft - 1);
  const top = Math.max(0, ownTop - 1);
  const right = Math.min(columns, ownRight + 1);
  const bottom = Math.min(rows, ownBottom + 1);
  const cells: number[] = [];
  for (let row = top; row < bottom; row++) {
    for (let column = left; column < right; column++) {
      if (column >= ownLeft && column < ownRight && row >= ownTop && row < ownBottom) {
        cells.push(row * columns + column);
      }
    }
  }
  for (let row = top; row < bottom; row++) {
    for (let column = left; column < right; column++) {
      if (column < ownLeft || column >= ownRight || row < ownTop || row >= ownBottom) {
        cells.push(row * columns + column);
      }
    }
  }
  return cells;
}
